// src/lib/forfeoNetwork.ts

/**
 * Fondation commune Forfeo Network pour Forfeo Explore.
 *
 * Objectif :
 * - Centraliser les badges, statuts, niveaux partenaires et deep links mobile.
 * - Rester autonome et compatible navigateur/Vite.
 * - Ne fait aucun appel Supabase direct.
 */

export type ForfeoPartnerTier =
  | 'listed'
  | 'certified'
  | 'premium'
  | 'signature'
  | 'regional_partner';

export type ForfeoProductKind =
  | 'experience'
  | 'gift_card'
  | 'universal_gift_card'
  | 'last_call'
  | 'bundle'
  | 'mystery'
  | 'corporate'
  | 'subscription'
  | 'service';

export type ForfeoNetworkStatus =
  | 'draft'
  | 'listed'
  | 'active'
  | 'paid'
  | 'confirmed'
  | 'reserved'
  | 'redeemed'
  | 'cancelled'
  | 'refunded'
  | 'expired'
  | 'disputed'
  | 'partner_payout_pending'
  | 'partner_paid'
  | 'manual_review';

export type ForfeoBadgeKind =
  | 'certified'
  | 'premium'
  | 'signature'
  | 'qr_secure'
  | 'wallet_ready'
  | 'last_call'
  | 'mystery'
  | 'corporate'
  | 'forfeo_guaranteed'
  | 'mobile_ready'
  | 'partner_payout';

export type ForfeoSalesChannel =
  | 'forfeo_com'
  | 'forfeo_explore'
  | 'mobile_app'
  | 'corporate'
  | 'merchant_dashboard'
  | 'admin'
  | 'unknown';

export type ForfeoPaymentFlow =
  | 'partner_collects'
  | 'forfeo_collects_manual_payout'
  | 'forfeo_collects_stripe_connect'
  | 'corporate_invoice'
  | 'gift_card_redemption';

export type ForfeoDeepLinkTarget =
  | 'experience'
  | 'reservation'
  | 'wallet'
  | 'gift_card'
  | 'last_call'
  | 'bundle'
  | 'merchant_scan'
  | 'support';

export type ForfeoDeepLinkOptions = {
  target: ForfeoDeepLinkTarget;
  id?: string | number | null;
  slug?: string | null;
  source?: ForfeoSalesChannel;
  fallbackUrl?: string;
};

export type ForfeoNetworkMoneySplit = {
  grossAmount: number;
  currency: string;
  stripeEstimatedFees: number;
  forfeoCommissionRate: number;
  forfeoCommissionAmount: number;
  regionalPartnerRate: number;
  regionalPartnerAmount: number;
  partnerNetAmount: number;
  estimatedForfeoNetAfterStripe: number;
};

export type ForfeoNetworkCommercialRule = {
  productKind: ForfeoProductKind;
  label: string;
  defaultCommissionRate: number;
  recommendedMinRate: number;
  recommendedMaxRate: number;
  recommendedPaymentFlow: ForfeoPaymentFlow;
  partnerPayoutTiming: string;
  note: string;
};

export const FORFEO_NETWORK_VERSION = '1.2.4';

export const FORFEO_NETWORK_BRAND = {
  name: 'Forfeo Network',
  shortName: 'Forfeo',
  tagline: 'Le réseau premium d’expériences locales certifiées.',
  customerPromise:
    'Achetez, réservez et vivez des expériences locales avec la confiance Forfeo.',
  partnerPromise:
    'Recevez des ventes qualifiées, une visibilité premium et des paiements suivis par Forfeo.',
  corporatePromise:
    'Offrez des expériences locales, cartes cadeaux et avantages corporatifs dans un réseau certifié.',
};

export const FORFEO_PARTNER_TIER_LABELS: Record<ForfeoPartnerTier, string> = {
  listed: 'Partenaire listé',
  certified: 'Partenaire certifié',
  premium: 'Partenaire Premium',
  signature: 'Partenaire Signature',
  regional_partner: 'Président Régional Partenaire',
};

export const FORFEO_PARTNER_TIER_DESCRIPTIONS: Record<ForfeoPartnerTier, string> = {
  listed:
    'Commerce ou expérience visible dans l’écosystème Forfeo, sans paiement centralisé obligatoire.',
  certified:
    'Partenaire validé par Forfeo, admissible aux réservations, pass, QR codes et ventes centralisées.',
  premium:
    'Partenaire prioritaire avec visibilité accrue, standards renforcés et offres exclusives.',
  signature:
    'Partenaire stratégique intégré aux expériences haut de gamme, bundles, corporate et campagnes Forfeo.',
  regional_partner:
    'Opérateur de territoire responsable du développement commercial, de la qualité et de l’animation locale.',
};

export const FORFEO_STATUS_LABELS: Record<ForfeoNetworkStatus, string> = {
  draft: 'Brouillon',
  listed: 'Listé',
  active: 'Actif',
  paid: 'Payé',
  confirmed: 'Confirmé',
  reserved: 'Réservé',
  redeemed: 'Utilisé',
  cancelled: 'Annulé',
  refunded: 'Remboursé',
  expired: 'Expiré',
  disputed: 'Litige',
  partner_payout_pending: 'Paiement partenaire à venir',
  partner_paid: 'Partenaire payé',
  manual_review: 'Révision manuelle',
};

export const FORFEO_PRODUCT_KIND_LABELS: Record<ForfeoProductKind, string> = {
  experience: 'Expérience locale',
  gift_card: 'Carte cadeau',
  universal_gift_card: 'Carte cadeau universelle Forfeo',
  last_call: 'Last Call',
  bundle: 'Bundle / forfait',
  mystery: 'Expérience mystère',
  corporate: 'Programme corporatif',
  subscription: 'Abonnement',
  service: 'Service',
};

export const FORFEO_BADGES: Record<
  ForfeoBadgeKind,
  { label: string; shortLabel: string; description: string }
> = {
  certified: {
    label: 'Certifié Forfeo',
    shortLabel: 'Certifié',
    description: 'Partenaire validé selon les standards de qualité Forfeo.',
  },
  premium: {
    label: 'Partenaire Premium',
    shortLabel: 'Premium',
    description: 'Partenaire avec visibilité accrue et expérience renforcée.',
  },
  signature: {
    label: 'Signature Forfeo',
    shortLabel: 'Signature',
    description: 'Expérience stratégique ou haut de gamme sélectionnée par Forfeo.',
  },
  qr_secure: {
    label: 'QR sécurisé',
    shortLabel: 'QR',
    description: 'Pass validable par QR code unique et suivi dans l’écosystème Forfeo.',
  },
  wallet_ready: {
    label: 'Wallet mobile prêt',
    shortLabel: 'Wallet',
    description: 'Compatible avec le wallet Forfeo et les pass expériences.',
  },
  last_call: {
    label: 'Last Call',
    shortLabel: 'Last Call',
    description: 'Offre de dernière minute ou inventaire à disponibilité limitée.',
  },
  mystery: {
    label: 'Expérience mystère',
    shortLabel: 'Mystère',
    description: 'L’identité du partenaire peut être révélée après réservation.',
  },
  corporate: {
    label: 'Corporate',
    shortLabel: 'Corporate',
    description: 'Admissible aux programmes cadeaux, employés ou clients d’entreprise.',
  },
  forfeo_guaranteed: {
    label: 'Garanti par Forfeo',
    shortLabel: 'Garanti',
    description: 'Forfeo agit comme tiers de confiance pour l’achat et le suivi.',
  },
  mobile_ready: {
    label: 'Réservable dans l’app',
    shortLabel: 'App',
    description: 'Expérience pensée pour l’application mobile Forfeo.',
  },
  partner_payout: {
    label: 'Paiement partenaire suivi',
    shortLabel: 'Paiement',
    description: 'Montant partenaire traçable dans le modèle Forfeo Network.',
  },
};

export const FORFEO_NETWORK_COMMERCIAL_RULES: ForfeoNetworkCommercialRule[] = [
  {
    productKind: 'experience',
    label: 'Expérience standard',
    defaultCommissionRate: 0.15,
    recommendedMinRate: 0.1,
    recommendedMaxRate: 0.2,
    recommendedPaymentFlow: 'forfeo_collects_manual_payout',
    partnerPayoutTiming: 'Après réservation confirmée ou utilisation validée.',
    note: 'Base recommandée pour les expériences locales classiques.',
  },
  {
    productKind: 'last_call',
    label: 'Last Call',
    defaultCommissionRate: 0.3,
    recommendedMinRate: 0.25,
    recommendedMaxRate: 0.35,
    recommendedPaymentFlow: 'forfeo_collects_manual_payout',
    partnerPayoutTiming: 'Après validation de la réservation ou utilisation.',
    note: 'Taux plus élevé justifié par l’écoulement d’inventaire invendu.',
  },
  {
    productKind: 'bundle',
    label: 'Bundle collaboratif',
    defaultCommissionRate: 0.25,
    recommendedMinRate: 0.2,
    recommendedMaxRate: 0.35,
    recommendedPaymentFlow: 'forfeo_collects_manual_payout',
    partnerPayoutTiming: 'Après confirmation des composantes du bundle.',
    note: 'La marge peut être calculée globalement plutôt que partenaire par partenaire.',
  },
  {
    productKind: 'universal_gift_card',
    label: 'Carte cadeau universelle',
    defaultCommissionRate: 0.08,
    recommendedMinRate: 0.05,
    recommendedMaxRate: 0.1,
    recommendedPaymentFlow: 'gift_card_redemption',
    partnerPayoutTiming: 'À l’utilisation ou selon cycle de règlement.',
    note: 'Modèle recommandé pour le wallet et les cartes cadeaux réseau.',
  },
];

const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export function calculateForfeoNetworkMoneySplit({
  grossAmount,
  currency = 'CAD',
  forfeoCommissionRate = 0.15,
  regionalPartnerRate = 0,
  stripeEstimatedFeeRate = 0.029,
  stripeEstimatedFixedFee = 0.3,
}: {
  grossAmount: number;
  currency?: string;
  forfeoCommissionRate?: number;
  regionalPartnerRate?: number;
  stripeEstimatedFeeRate?: number;
  stripeEstimatedFixedFee?: number;
}): ForfeoNetworkMoneySplit {
  const safeGross = Number.isFinite(grossAmount) ? Math.max(0, grossAmount) : 0;
  const stripeEstimatedFees = roundMoney(safeGross * stripeEstimatedFeeRate + stripeEstimatedFixedFee);
  const forfeoCommissionAmount = roundMoney(safeGross * forfeoCommissionRate);
  const regionalPartnerAmount = roundMoney(safeGross * regionalPartnerRate);
  const partnerNetAmount = roundMoney(
    Math.max(0, safeGross - forfeoCommissionAmount - regionalPartnerAmount)
  );
  const estimatedForfeoNetAfterStripe = roundMoney(
    Math.max(0, forfeoCommissionAmount - stripeEstimatedFees)
  );

  return {
    grossAmount: roundMoney(safeGross),
    currency,
    stripeEstimatedFees,
    forfeoCommissionRate,
    forfeoCommissionAmount,
    regionalPartnerRate,
    regionalPartnerAmount,
    partnerNetAmount,
    estimatedForfeoNetAfterStripe,
  };
}

export const calculateForfeoNetworkSplit = calculateForfeoNetworkMoneySplit;

export function getForfeoNetworkBadges(input?: {
  isCertified?: boolean;
  isPremium?: boolean;
  isSignature?: boolean;
  isWalletReady?: boolean;
  isQrSecure?: boolean;
  isLastCall?: boolean;
  isMystery?: boolean;
  isCorporate?: boolean;
  isMobileReady?: boolean;
}): ForfeoBadgeKind[] {
  const badges: ForfeoBadgeKind[] = [];

  if (input?.isCertified !== false) badges.push('certified');
  if (input?.isPremium) badges.push('premium');
  if (input?.isSignature) badges.push('signature');
  if (input?.isWalletReady !== false) badges.push('wallet_ready');
  if (input?.isQrSecure !== false) badges.push('qr_secure');
  if (input?.isLastCall) badges.push('last_call');
  if (input?.isMystery) badges.push('mystery');
  if (input?.isCorporate) badges.push('corporate');
  if (input?.isMobileReady !== false) badges.push('mobile_ready');
  badges.push('forfeo_guaranteed');

  return Array.from(new Set(badges));
}

export function buildForfeoMobileDeepLink(options: ForfeoDeepLinkOptions): string {
  const target = options.target || 'support';
  const params = new URLSearchParams();

  if (options.id !== undefined && options.id !== null) params.set('id', String(options.id));
  if (options.slug) params.set('slug', options.slug);
  if (options.source) params.set('source', options.source);
  params.set('utm_source', 'forfeo_explore');
  params.set('utm_medium', 'web_to_app');
  params.set('utm_campaign', 'forfeo_network_lot4');

  const query = params.toString();
  return `forfeo://${target}${query ? `?${query}` : ''}`;
}

export function buildForfeoExploreFallbackUrl(options: ForfeoDeepLinkOptions): string {
  if (options.fallbackUrl) return options.fallbackUrl;

  const idOrSlug = options.slug || options.id;

  switch (options.target) {
    case 'experience':
      return idOrSlug ? `/boutique/${idOrSlug}` : '/boutique';
    case 'reservation':
      return idOrSlug ? `/reservation/${idOrSlug}` : '/mes-reservations';
    case 'last_call':
      return '/last-call';
    case 'gift_card':
      return '/carte-cadeau';
    case 'bundle':
      return idOrSlug ? `/combos/${idOrSlug}` : '/combos';
    case 'wallet':
      return '/mes-achats';
    case 'merchant_scan':
      return '/business-dashboard';
    case 'support':
    default:
      return '/aide';
  }
}

export function openForfeoMobileDeepLink(options: ForfeoDeepLinkOptions) {
  const deepLink = buildForfeoMobileDeepLink(options);
  const fallbackUrl = buildForfeoExploreFallbackUrl(options);

  if (typeof window === 'undefined') {
    return { deepLink, fallbackUrl };
  }

  window.location.href = deepLink;

  window.setTimeout(() => {
    if (document.visibilityState === 'visible') {
      window.location.href = fallbackUrl;
    }
  }, 900);

  return { deepLink, fallbackUrl };
}
